import type { HubGuideArticleProps } from "@/types/help-hub/presentation";
import { hubBrandTheme } from "../brandInk";
import { toBlocks, type GuideBlock } from "../helpGuideBlocks";

/** Eyebrow label above the title, per branded-document type. */
const TEMPLATE_EYEBROW: Record<string, string> = {
  faq: "Frequently Asked Questions",
  "how-to": "Step-by-Step Guide",
  "one-pager": "Overview",
};

const initialOf = (name: string): string =>
  name.trim().charAt(0).toUpperCase() || "G";

/** Inline pass: `**bold**` becomes real emphasis, never raw asterisks. */
const renderInline = (text: string): (string | JSX.Element)[] => {
  const parts: (string | JSX.Element)[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let last = 0;
  let key = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(
      <strong key={key++} className="font-semibold text-genie-text-primary">
        {match[1]}
      </strong>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
};

/**
 * One published guide rendered as the same co-branded document the PDF export
 * produces — customer masthead, brand gradient rule, eyebrow, numbered
 * sections, "Powered by Help Genie" footer. What you read on the page is what
 * you get when you download it.
 *
 * Two content paths, one look:
 *   - A branded document (`guide.genieDocument`) renders from its structured
 *     sections — literally the PDF's source of truth.
 *   - An uploaded source renders through `toBlocks`, with its top-level
 *     headings numbered the same way the PDF numbers its sections.
 */
export const HubGuideArticle = ({
  guide,
  branding,
  displayName,
  action,
}: HubGuideArticleProps) => {
  const theme = hubBrandTheme(branding);
  const primary = theme.primary;
  const gradient = theme.gradient;

  const eyebrow = guide.genieDocument
    ? TEMPLATE_EYEBROW[guide.genieDocument.template] ?? "Guide"
    : guide.topic?.name || "Guide";

  const sectionChip = (label: string | number) => (
    <div
      className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
      style={{ background: gradient }}
    >
      {label}
    </div>
  );

  /**
   * Render parsed blocks. With `numberSections` (uploaded sources) top-level
   * headings get the PDF's numbered gradient chips and everything after the
   * first one indents to align under the heading text; without it (a branded
   * document's own section body) headings render as plain bold lines, because
   * the section chrome already carries the number.
   */
  const renderBlocks = (blocks: GuideBlock[], numberSections: boolean) => {
    let sectionNumber = 0;
    let seenSection = false;
    return blocks.map((block, index) => {
      const indented = numberSections && seenSection;
      if (block.kind === "heading") {
        if (numberSections && block.level === 2) {
          sectionNumber += 1;
          seenSection = true;
          return (
            <div key={index} className="flex gap-4 pt-2 first:pt-0">
              {sectionChip(sectionNumber)}
              <h2 className="text-[17px] font-semibold leading-snug text-genie-text-primary sm:pt-1">
                {renderInline(block.value)}
              </h2>
            </div>
          );
        }
        return (
          <h3
            key={index}
            className={`pt-1 text-[15px] font-semibold text-genie-text-primary ${indented ? "sm:pl-12" : ""}`}
          >
            {renderInline(block.value)}
          </h3>
        );
      }
      if (block.kind === "list") {
        const items = block.items.map((item, itemIndex) => (
          <li key={itemIndex} className="mb-1">
            {renderInline(item)}
          </li>
        ));
        const listClass = `pl-5 text-[15px] leading-relaxed text-genie-text-sub ${indented ? "sm:ml-12" : ""}`;
        return block.ordered ? (
          <ol key={index} className={`list-decimal ${listClass}`}>
            {items}
          </ol>
        ) : (
          <ul key={index} className={`list-disc ${listClass}`}>
            {items}
          </ul>
        );
      }
      return (
        <p
          key={index}
          className={`whitespace-pre-line text-[15px] leading-relaxed text-genie-text-sub ${indented ? "sm:pl-12" : ""}`}
        >
          {renderInline(block.value)}
        </p>
      );
    });
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-genie-border bg-white shadow-sm">
      {/* The customer's colours from the very first pixel — same as the PDF. */}
      <div aria-hidden className="h-1.5 w-full" style={{ background: gradient }} />

      <div className="p-6 sm:p-12">
        {/* Co-branded masthead — customer leads (left), Help Genie present (right). */}
        <div className="flex items-start justify-between gap-4 pb-6">
          <div className="flex min-w-0 items-center gap-4">
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt=""
                className="h-16 w-16 shrink-0 rounded-xl bg-white object-contain ring-1 ring-black/[0.06]"
              />
            ) : (
              <div
                className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl text-2xl font-bold text-white"
                style={{ background: gradient }}
              >
                {initialOf(displayName)}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate text-xl font-bold leading-tight text-genie-text-primary">
                {displayName}
              </p>
              <p className="mt-0.5 truncate text-xs text-genie-text-muted">Help Centre</p>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
            <span className="text-[10px] uppercase tracking-[0.15em] text-genie-text-muted">
              Made with
            </span>
            <img src="/helpgenie.png" alt="Help Genie" className="h-[18px] w-auto" />
          </div>
        </div>

        {/* Signature gradient rule in the customer's colours. */}
        <div aria-hidden className="h-1.5 w-full rounded-full" style={{ background: gradient }} />

        {/* Title block. */}
        <div className="mb-9 mt-8">
          <p
            className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.18em]"
            style={{ color: primary }}
          >
            {eyebrow}
          </p>
          <h1 className="text-[clamp(24px,3.4vw,32px)] font-bold leading-[1.15] text-genie-text-primary">
            {guide.genieDocument?.title ?? guide.name}
          </h1>
          {(guide.genieDocument?.subtitle ?? guide.summary) && (
            <p className="mt-2.5 text-base text-genie-text-sub">
              {guide.genieDocument?.subtitle ?? guide.summary}
            </p>
          )}
          {action && <div className="mt-5">{action}</div>}
        </div>

        {/* Body. */}
        {guide.genieDocument ? (
          <div className="hub-guide-body space-y-7">
            {guide.genieDocument.sections.map((section, index) => (
              <section key={index} id={`guide-section-${index + 1}`} className="flex gap-4">
                {sectionChip(index + 1)}
                <div className="min-w-0 flex-1">
                  <h2 className="mb-1.5 text-[17px] font-semibold leading-snug text-genie-text-primary">
                    {renderInline(section.heading)}
                  </h2>
                  <div className="space-y-3">
                    {renderBlocks(toBlocks(section.body, false), false)}
                  </div>
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="hub-guide-body space-y-4">{renderBlocks(toBlocks(guide.content), true)}</div>
        )}

        {guide.truncated && (
          <p className="mt-8 border-t border-genie-border pt-4 text-[13px] text-genie-text-muted">
            This guide is longer than the page shows. Ask the Genie if you need the
            part that isn't here.
          </p>
        )}
      </div>

      {/* Co-brand footer — mirrors the PDF's closing line. */}
      <div className="flex items-center justify-between gap-4 border-t border-genie-border px-6 py-4 sm:px-12">
        <div className="flex min-w-0 items-center gap-2">
          <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: gradient }} />
          <span className="truncate text-xs text-genie-text-muted">{displayName}</span>
        </div>
        <a href="/" className="flex shrink-0 items-center gap-2" aria-label="Powered by Help Genie">
          <span className="text-[11px] text-genie-text-muted">Powered by</span>
          <img src="/helpgenie.png" alt="Help Genie" className="h-4 w-auto" />
        </a>
      </div>
    </div>
  );
};

export default HubGuideArticle;
